// Player Controls
import { PauseIcon, PlayIcon } from '@heroicons/react/24/solid';

export const PlayerControls = ({ audioRef, playing, setPlaying, mini }) => (
  <>
    {/* Rewind button - jumps back 10 seconds with animation */}
    <button
      onClick={(e) => {
        // Animate the icon with a rotation effect
        const icon = e.target.closest('button').querySelector('div');
        icon.classList.remove('rotate-90');
        setTimeout(() => {
          icon.classList.add('rotate-90');
        }, 300);
        // Rewind audio by 10 seconds
        audioRef.current.currentTime -= 10;
      }}
      onKeyDown={(e) => {
        // Support keyboard activation
        if (e.key === 'Enter' || e.key === ' ') {
          audioRef.current.currentTime -= 10;
          e.preventDefault();
        }
      }}
      className={`${mini ? 'col-start-1 row-start-3' : 'col-start-1 row-start-3'} focus-visible:outline-pdTeal relative h-px-8 w-px-8 cursor-pointer focus-visible:outline-2`}
      aria-label="Rewind 10 seconds"
    >
      <div className="rotate-90 fill-neutral-800 transition-[rotate] duration-500 ease-in-out">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
          <path d="M88 256L232 256C241.7 256 250.5 250.2 254.2 241.2C257.9 232.2 255.9 221.9 249 215L202.3 168.3C277.6 109.7 386.6 115 455.8 184.2C530.8 259.2 530.8 380.7 455.8 455.7C380.8 530.7 259.3 530.7 184.3 455.7C174.1 445.5 165.3 434.4 157.9 422.7C148.4 407.8 128.6 403.4 113.7 412.9C98.8 422.4 94.4 442.2 103.9 457.1C113.7 472.7 125.4 487.5 139 501C239 601 401 601 501 501C601 401 601 239 501 139C406.8 44.7 257.3 39.3 156.7 122.8L105 71C98.1 64.2 87.8 62.1 78.8 65.8C69.8 69.5 64 78.3 64 88L64 232C64 245.3 74.7 256 88 256z" />
        </svg>
      </div>
      <span
        className="font bolduppercase absolute top-1/2 left-1/2 -translate-1/2 pt-[2px] text-[9.6px]"
        aria-hidden="true"
      >
        10
      </span>
    </button>

    {/* Play/Pause button - toggles between play and pause states with animation */}
    <button
      className={`${mini ? 'col-start-2 row-start-3' : 'col-start-2 row-start-3'} focus-visible:outline-pdTeal relative flex cursor-pointer items-center focus-visible:outline-2`}
      onClick={() => {
        // Toggle play/pause state
        if (playing) {
          audioRef.current.pause();
        } else {
          audioRef.current.play();
        }
        setPlaying(!playing);
      }}
      onKeyDown={(e) => {
        // Support keyboard activation
        if (e.key === 'Enter' || e.key === ' ') {
          if (playing) {
            audioRef.current.pause();
          } else {
            audioRef.current.play();
          }
          setPlaying(!playing);
          e.preventDefault();
        }
      }}
      aria-label={playing ? 'Pause' : 'Play'}
      aria-pressed={playing} // ARIA state for toggle buttons
    >
      {/* Play icon - visible when paused, hidden when playing */}
      <span
        className={`${playing ? '-rotate-90 opacity-0' : 'opacity-100'} absolute top-1/2 left-1/2 -translate-1/2 transition-all duration-300 ease-in-out`}
        aria-hidden="true"
      >
        <PlayIcon className="h-px-7 w-px-7" />
      </span>
      {/* Pause icon - visible when playing, hidden when paused */}
      <span
        className={`${playing ? 'opacity-100' : 'rotate-90 opacity-0'} absolute top-1/2 left-1/2 -translate-1/2 transition-all duration-300 ease-in-out`}
        aria-hidden="true"
      >
        <PauseIcon className="h-px-7 w-px-7" />
      </span>
    </button>

    {/* Forward button - jumps ahead 30 seconds with animation */}
    <button
      onClick={(e) => {
        // Animate the icon with a rotation effect

        const icon = e.target.closest('button').querySelector('div');
        icon.classList.remove('-rotate-90');
        setTimeout(() => {
          icon.classList.add('-rotate-90');
        }, 300);
        // Forward audio by 30 seconds
        audioRef.current.currentTime += 30;
      }}
      onKeyDown={(e) => {
        // Support keyboard activation
        if (e.key === 'Enter' || e.key === ' ') {
          audioRef.current.currentTime += 30;
          e.preventDefault();
        }
      }}
      className={`${mini ? 'col-start-3 row-start-3' : 'col-start-3 row-start-3'} focus-visible:outline-pdTeal relative h-px-8 w-px-8 cursor-pointer focus-visible:outline-2`}
      aria-label="Forward 30 seconds"
    >
      <div className="-rotate-90 fill-neutral-800 transition-[rotate] duration-500 ease-in-out">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
          <path d="M552 256L408 256C398.3 256 389.5 250.2 385.8 241.2C382.1 232.2 384.1 221.9 391 215L437.7 168.3C362.4 109.7 253.4 115 184.2 184.2C109.2 259.2 109.2 380.7 184.2 455.7C259.2 530.7 380.7 530.7 455.7 455.7C463.9 447.5 471.2 438.8 477.6 429.6C487.7 415.1 507.7 411.6 522.2 421.7C536.7 431.8 540.2 451.8 530.1 466.3C521.6 478.5 511.9 490.1 501 501C401 601 238.9 601 139 501C39.1 401 39 239 139 139C233.3 44.7 382.7 39.4 483.3 122.8L535 71C541.9 64.1 552.2 62.1 561.2 65.8C570.2 69.5 576 78.3 576 88L576 232C576 245.3 565.3 256 552 256z" />
        </svg>
      </div>
      <span
        className="font bolduppercase absolute top-1/2 left-1/2 -translate-1/2 pt-[2px] pl-[1px] text-[9.6px]"
        aria-hidden="true"
      >
        30
      </span>
    </button>
  </>
);